import React, { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import SearchBar from '../SearchBar';
import Input from '../Input';
import ButtonSmall from '../ButtonSmall';
import { KeyboardKeys } from '../../../../../../constants';
import styles from './DashboardSearch.module.scss';
import { SearchIcon } from 'app/scripts/icons';

interface ISelectOption {
  label: string;
  value: string;

  [key: string]: any;
}

interface IDashboardSearch {
  className?: string;
  searchByOptions: ISelectOption[];
  defaultSearchByValue?: string;
  searchByValue: string;
  value: string;
  optionsLoaders?: {
    [value: string]: (params: {
      inputValue: string;
      searchBy?: string;
      callback?: (options: ISelectOption[]) => void;
    }) => Promise<ISelectOption[]>;
  };
  onSearchByChange: (searchByValue: string) => void;
  onSearch: (value: string) => void;
  onExpand?: () => void;
  onCollapse?: () => void;
}

const DashboardSearch = (props: IDashboardSearch): JSX.Element => {
  const {
    className,
    searchByOptions,
    defaultSearchByValue = searchByOptions[0].value,
    searchByValue,
    value = '',
    onSearchByChange,
    optionsLoaders,
    onExpand,
    onCollapse,
    onSearch
  } = props;
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState(value);

  // FYI: We need this useEffect to be able to update DashboardSearch value externally @kyivcoder
  useEffect(() => {
    setSearchValue(value);
  }, [value]);

  const onSearchBarCollapse = useCallback(() => {
    setIsSearchExpanded(false);
    onCollapse();
  }, [onCollapse]);

  const handleSearch = useCallback(
    (value: string) => {
      onSearchBarCollapse();
      onSearch(value);
    },
    [onSearch, onSearchBarCollapse]
  );

  const onSearchBarExpand = useCallback(() => {
    setIsSearchExpanded(true);
    onExpand();
  }, [onExpand]);

  const onSearchBarSelect = useCallback(
    (value: string) => {
      setSearchValue(value);
      handleSearch(value);
    },
    [handleSearch]
  );

  const onSearchBarKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement | HTMLInputElement>) => {
      switch (event.key) {
        case KeyboardKeys.Enter:
          handleSearch(searchValue);
          break;
        case KeyboardKeys.Esc:
        case KeyboardKeys.Escape:
          onSearchBarCollapse();
          break;
        default:
          return;
      }
    },
    [handleSearch, onSearchBarCollapse, searchValue]
  );

  const actionButtons = (
    <>
      <ButtonSmall onClick={() => handleSearch(searchValue)} disabled={!searchValue}>
        Search
      </ButtonSmall>
      <ButtonSmall variant="action" onClick={onSearchBarCollapse}>
        Back
      </ButtonSmall>
    </>
  );

  return (
    <div className={cx(styles.dashboardSearch, className)}>
      {!isSearchExpanded && (
        <Input
          className={cx(styles.searchInput, { [styles.searchInputFilled]: !!searchValue })}
          data-testid="search-input"
          isBorderless
          value={searchValue}
          placeholder="Search..."
          addonBefore={<SearchIcon />}
          onClick={onSearchBarExpand}
        />
      )}

      {isSearchExpanded && (
        <SearchBar
          autofocus
          searchByOptions={searchByOptions}
          optionsLoaders={optionsLoaders}
          actionButtons={actionButtons}
          value={searchValue}
          searchByValue={searchByValue}
          onSearchByChange={onSearchByChange}
          onChange={setSearchValue}
          onSelect={onSearchBarSelect}
          onKeyDown={onSearchBarKeyDown}
        />
      )}
    </div>
  );
};

export default DashboardSearch;
